import { format, formatDistanceToNowStrict } from 'date-fns';
import { Check, Circle, Clock, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PARTY_LABELS } from '@/lib/agreements/constants';
import type { AgreementSignatureRow } from '@/lib/agreements/api';

/**
 * One chip per party, in signing order, saying what that party is waiting on.
 *
 * The four states are the ones an admin acts on differently: nothing sent yet, sent and
 * elapsing, signed with its timestamp, and declined — which blocks the document until it
 * is voided and reissued.
 */
function chipCopy(signature: AgreementSignatureRow): { note: string; icon: typeof Check; tone: string } {
    switch (signature.status) {
        case 'SIGNED':
            return {
                note: signature.signed_at ? format(new Date(signature.signed_at), 'MMM d, yyyy · HH:mm') : 'signed',
                icon: Check,
                tone: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
            };
        case 'DECLINED':
            return {
                note: 'declined',
                icon: X,
                tone: 'border-destructive/30 bg-destructive/10 text-destructive',
            };
        case 'VOIDED':
            return {
                note: 'voided',
                icon: X,
                tone: 'border-border bg-muted text-muted-foreground line-through',
            };
        case 'SENT':
        case 'VIEWED':
            return {
                note: signature.status === 'VIEWED' ? 'opened' : 'awaiting signature',
                icon: Clock,
                tone: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
            };
        default:
            return {
                note: 'not sent',
                icon: Circle,
                tone: 'border-border bg-muted/60 text-muted-foreground',
            };
    }
}

interface PartyChipsProps {
    signatures: AgreementSignatureRow[];
    /** When the document went out, used for the elapsed time on an awaiting chip. */
    since?: string | null;
    /**
     * Per-party labels for a document type that reads a seat differently — the deposit arc's
     * DRIVER seat is the depositor, who holds no driver row. Anything absent falls through to
     * the shared vocabulary, so an override is a sentence about one seat, never a second map
     * of all of them.
     */
    labels?: Record<string, string>;
    className?: string;
}

export default function PartyChips({ signatures, since, labels, className }: PartyChipsProps) {
    const ordered = [...signatures].sort((a, b) => (a.sign_order ?? 0) - (b.sign_order ?? 0));

    return (
        <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
            {ordered.map((signature) => {
                const { note, icon: Icon, tone } = chipCopy(signature);
                const elapsed =
                    (signature.status === 'SENT' || signature.status === 'VIEWED') && since
                        ? ` · ${formatDistanceToNowStrict(new Date(since), { addSuffix: false })}`
                        : '';

                return (
                    <span
                        key={signature.party}
                        className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                            tone,
                        )}
                    >
                        <Icon className="h-3 w-3 shrink-0" />
                        <span className="font-medium">
                            {labels?.[signature.party] ?? PARTY_LABELS[signature.party] ?? signature.party}
                        </span>
                        <span className="opacity-70">
                            · {note}
                            {elapsed}
                        </span>
                    </span>
                );
            })}
        </div>
    );
}
